class HomepageUgcReviews extends HTMLElement {
  connectedCallback() {
    this.slider = this.querySelector('[data-slider]');
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.hideControlsMs = 1400;
    this.settleMs = 620;
    this.onPopupKeydown = this.onPopupKeydown.bind(this);

    if (!this.slider) return;

    this.swipeDesktop = Math.max(1, parseInt(this.dataset.swipeDesktop, 10) || 1);
    this.swipeMobile = Math.max(1, parseInt(this.dataset.swipeMobile, 10) || 1);
    this.setupLoop();
    this.mediaItems = Array.from(this.querySelectorAll('[data-ugc-media]'));
    if (this.cards.length === 0) return;

    this.onResize = this.onResize.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onWheel = this.onWheel.bind(this);

    window.addEventListener('resize', this.onResize);

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goTo(this.realStart + index));
    });

    this.enableDrag();
    this.bindMedia();
    requestAnimationFrame(() => this.goTo(this.activeIndex, false));
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    this.disableDrag();
    this.clearHideTimers();
    this.cancelSettle();
    this.closePopup();
    this.popup?.remove();
    document.removeEventListener('keydown', this.onPopupKeydown);
  }

  setupLoop() {
    this.slider.querySelectorAll('[data-clone]').forEach((node) => node.remove());

    const realCards = Array.from(this.slider.querySelectorAll('[data-card]'));
    this.realCount = realCards.length;
    this.looping = this.realCount > 0;
    realCards.forEach((card) => {
      card.querySelectorAll('img').forEach((img) => {
        img.loading = 'eager';
        img.removeAttribute('loading');
      });
    });
    this.copiesEachSide = Math.max(
      2,
      Math.ceil((3 + Math.max(this.swipeDesktop, this.swipeMobile)) / Math.max(this.realCount, 1))
    );

    if (this.looping) {
      const prefix = document.createDocumentFragment();
      const suffix = document.createDocumentFragment();

      for (let copy = 0; copy < this.copiesEachSide; copy++) {
        realCards.forEach((card) => prefix.appendChild(this.cloneCard(card)));
      }
      for (let copy = 0; copy < this.copiesEachSide; copy++) {
        realCards.forEach((card) => suffix.appendChild(this.cloneCard(card)));
      }

      this.slider.insertBefore(prefix, realCards[0]);
      this.slider.appendChild(suffix);
    }

    this.cards = Array.from(this.slider.querySelectorAll('[data-card]'));
    this.realStart = this.looping ? this.realCount * this.copiesEachSide : 0;
    this.activeIndex = this.realStart + (this.realCount >= 3 ? 1 : 0);
    this.preloadSliderMedia();
  }

  preloadSliderMedia() {
    this.slider.querySelectorAll('img').forEach((img) => {
      img.loading = 'eager';
      img.removeAttribute('loading');
      if (typeof img.decode === 'function') img.decode().catch(() => {});
    });
  }

  cloneCard(card) {
    const clone = card.cloneNode(true);
    clone.setAttribute('data-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('data-shopify-editor-block');
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    const sourceImages = card.querySelectorAll('img');
    clone.querySelectorAll('img').forEach((img, index) => {
      img.loading = 'eager';
      img.removeAttribute('loading');
      const source = sourceImages[index];
      const url = source?.currentSrc || source?.src;
      if (url) img.src = url;
    });
    return clone;
  }

  bindMedia() {
    this.popup =
      this.closest('.shopify-section')?.querySelector('[data-ugc-popup]') ||
      document.getElementById(this.id?.replace('HomepageUgcReviews-', 'UgcReviewsPopup-'));
    this.popupWrap = this.popup?.querySelector('[data-popup-wrap]');
    this.popupVideo = this.popup?.querySelector('[data-popup-video]');
    this.popupToggle = this.popup?.querySelector('[data-popup-toggle]');
    this.popupMute = this.popup?.querySelector('[data-popup-mute]');

    if (this.popup && this.popup.parentElement !== document.body) {
      document.body.appendChild(this.popup);
    }

    this.mediaItems.forEach((media) => {
      const toggle = media.querySelector('[data-media-toggle]');
      const src = media.getAttribute('data-video-src');
      if (!toggle || !src) return;

      toggle.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (this.didDrag) return;
        this.openPopup(src, toggle);
      });

      toggle.addEventListener('click', (event) => {
        event.preventDefault();
      });

      toggle.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.openPopup(src, toggle);
      });
    });

    if (!this.popup || !this.popupVideo) return;

    this.popup.addEventListener('click', (event) => {
      if (event.target.closest('.homepage-ugc-reviews__popup-dialog')) return;
      this.closePopup();
    });

    this.popup.querySelector('.homepage-ugc-reviews__popup-close')?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.closePopup();
    });

    this.popupToggle?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.togglePopupVideo();
    });

    this.popupMute?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.togglePopupMute();
    });

    this.popupWrap?.addEventListener('click', (event) => {
      if (event.target.closest('[data-popup-close], [data-popup-mute], [data-popup-toggle]')) return;
      this.togglePopupVideo();
    });

    this.popupVideo.addEventListener('play', () => this.setPopupPlaying(true));
    this.popupVideo.addEventListener('pause', () => this.setPopupPlaying(false));
  }

  openPopup(src, trigger) {
    if (!this.popup || !this.popupVideo || !src) return;

    this.lastTrigger = trigger;
    this.popup.hidden = false;
    this.popup.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keydown', this.onPopupKeydown);
    this.popupVideo.muted = false;
    this.popup.classList.remove('is-muted');
    this.popupMute?.setAttribute('aria-label', 'Mute video');
    this.popupVideo.src = src;
    this.popupVideo.playsInline = true;
    const playPromise = this.popupVideo.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        this.popupVideo.muted = true;
        this.popup.classList.add('is-muted');
        this.popupMute?.setAttribute('aria-label', 'Unmute video');
        this.popupVideo.play().catch(() => {});
      });
    }
    this.popup.querySelector('[data-popup-close]')?.focus();
  }

  closePopup() {
    if (!this.popup) return;
    document.removeEventListener('keydown', this.onPopupKeydown);
    if (this.popupVideo) {
      this.popupVideo.pause();
      this.popupVideo.removeAttribute('src');
      this.popupVideo.load();
    }
    this.popup.classList.remove('is-open', 'is-muted');
    this.popup.hidden = true;
    this.popupWrap?.classList.remove('is-playing', 'is-controls-hidden');
    this.clearHideTimer(this.popupWrap);
    document.body.classList.remove('overflow-hidden');
    this.lastTrigger?.focus();
    this.lastTrigger = null;
  }

  onPopupKeydown(event) {
    if (event.key === 'Escape') this.closePopup();
  }

  togglePopupVideo() {
    if (!this.popupVideo) return;
    if (this.popupVideo.paused) {
      this.popupVideo.play().catch(() => {});
      return;
    }
    this.popupVideo.pause();
  }

  togglePopupMute() {
    if (!this.popupVideo) return;
    this.popupVideo.muted = !this.popupVideo.muted;
    this.popup.classList.toggle('is-muted', this.popupVideo.muted);
    this.popupMute?.setAttribute('aria-label', this.popupVideo.muted ? 'Unmute video' : 'Mute video');
  }

  setPopupPlaying(playing) {
    if (!this.popupWrap) return;
    this.popupWrap.classList.toggle('is-playing', playing);
    this.popupToggle?.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');

    if (!playing) {
      this.popupWrap.classList.remove('is-controls-hidden');
      this.clearHideTimer(this.popupWrap);
      return;
    }

    this.popupWrap.classList.remove('is-controls-hidden');
    if (window.matchMedia('(max-width: 749px)').matches) {
      this.scheduleHideControls(this.popupWrap);
    }
  }

  scheduleHideControls(media) {
    this.clearHideTimer(media);
    media._hideTimer = setTimeout(() => {
      if (media.classList.contains('is-playing')) {
        media.classList.add('is-controls-hidden');
      }
    }, this.hideControlsMs);
  }

  clearHideTimer(media) {
    if (!media) return;
    if (media._hideTimer) {
      clearTimeout(media._hideTimer);
      media._hideTimer = null;
    }
  }

  clearHideTimers() {
    this.clearHideTimer(this.popupWrap);
  }

  onResize() {
    this.goTo(this.activeIndex, false);
  }

  cardOffset(index) {
    const card = this.cards[index];
    if (!card) return 0;
    return card.offsetLeft - (this.slider.clientWidth - card.offsetWidth) / 2;
  }

  cardStep() {
    if (this.cards.length < 2) return this.cards[0]?.offsetWidth || 0;
    return Math.abs(this.cards[1].offsetLeft - this.cards[0].offsetLeft);
  }

  setActive(index) {
    this.activeIndex = index;
    this.cards.forEach((card, i) => card.classList.toggle('is-active', i === index));

    const realIndex =
      this.realCount > 0
        ? (((index - this.realStart) % this.realCount) + this.realCount) % this.realCount
        : index;
    this.dots.forEach((dot, i) => {
      const isActive = i === realIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  easeOutQuart(t) {
    const rest = 1 - t;
    return 1 - rest * rest * rest * rest;
  }

  cancelSettle() {
    if (this.settleFrame) {
      cancelAnimationFrame(this.settleFrame);
      this.settleFrame = null;
    }
    this.settling = false;
  }

  wrappedIndex(index) {
    if (!this.looping || this.realCount < 1) return index;
    let next = index;
    while (next < this.realStart) next += this.realCount;
    while (next >= this.realStart + this.realCount) next -= this.realCount;
    return next;
  }

  jumpTo(index) {
    this.setActive(index);
    this.slider.scrollLeft = this.cardOffset(index);
  }

  syncToReal() {
    if (!this.looping || this.realCount < 1) return;
    const wrapped = this.wrappedIndex(this.activeIndex);
    if (wrapped === this.activeIndex) return;
    this.jumpTo(wrapped);
  }

  normalizeIndex() {
    this.syncToReal();
  }

  goTo(index, animate = true) {
    const from = this.activeIndex;
    this.syncToReal();
    if (this.activeIndex !== from) {
      index = this.activeIndex + (index - from);
    }

    const next = Math.max(0, Math.min(this.cards.length - 1, index));
    const target = this.cardOffset(next);
    const maxScroll = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
    const clampedTarget = Math.max(0, Math.min(maxScroll, target));
    const blocked = Math.abs(clampedTarget - this.slider.scrollLeft) < 2 && next !== this.activeIndex;

    if (this.looping && blocked) {
      this.cancelSettle();
      this.jumpTo(this.wrappedIndex(next));
      return;
    }

    this.setActive(next);

    if (!animate) {
      this.cancelSettle();
      this.slider.scrollLeft = clampedTarget;
      this.normalizeIndex();
      return;
    }

    this.animateScroll(clampedTarget, () => this.normalizeIndex());
  }

  animateScroll(target, after) {
    this.cancelSettle();
    const start = this.slider.scrollLeft;
    const distance = target - start;
    if (Math.abs(distance) < 1) {
      after?.();
      return;
    }

    const duration = this.settleMs;
    const startTime = performance.now();
    this.settling = true;

    const step = (now) => {
      const t = Math.min(1, (now - startTime) / duration);
      this.slider.scrollLeft = start + distance * this.easeOutQuart(t);
      if (t < 1) {
        this.settleFrame = requestAnimationFrame(step);
        return;
      }
      this.slider.scrollLeft = target;
      this.settling = false;
      this.settleFrame = null;
      after?.();
    };

    this.settleFrame = requestAnimationFrame(step);
  }

  dragThreshold() {
    return Math.max(52, this.cardStep() * 0.22);
  }

  swipeStep() {
    return window.matchMedia('(max-width: 749px)').matches ? this.swipeMobile : this.swipeDesktop;
  }

  enableDrag() {
    this.slider.addEventListener('pointerdown', this.onPointerDown);
    this.slider.addEventListener('wheel', this.onWheel, { passive: false });
  }

  disableDrag() {
    this.slider.removeEventListener('pointerdown', this.onPointerDown);
    this.stopTracking();
    this.slider.removeEventListener('wheel', this.onWheel);
  }

  stopTracking() {
    if (this.pointerId != null && this.slider.hasPointerCapture(this.pointerId)) {
      this.slider.releasePointerCapture(this.pointerId);
    }
    this.pointerId = null;
    this.tracking = false;
    this.dragging = false;
    this.slider.classList.remove('is-dragging');
    this.slider.removeEventListener('pointermove', this.onPointerMove);
    this.slider.removeEventListener('pointerup', this.onPointerUp);
    this.slider.removeEventListener('pointercancel', this.onPointerUp);
  }

  finishSwipe(direction) {
    this.stopTracking();
    this.goTo(this.activeIndex + direction * this.swipeStep(), true);
  }

  onPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    if (this.settling) this.cancelSettle();
    this.syncToReal();
    this.slider.scrollLeft = this.cardOffset(this.activeIndex);

    this.tracking = true;
    this.dragging = false;
    this.didDrag = false;
    this.pointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragScrollLeft = this.slider.scrollLeft;
    this.slider.addEventListener('pointermove', this.onPointerMove);
    this.slider.addEventListener('pointerup', this.onPointerUp);
    this.slider.addEventListener('pointercancel', this.onPointerUp);
  }

  onPointerMove(event) {
    if (!this.tracking) return;
    const delta = event.clientX - this.dragStartX;

    if (!this.dragging) {
      if (Math.abs(delta) < 8) return;
      this.dragging = true;
      this.didDrag = true;
      this.cancelSettle();
      this.slider.classList.add('is-dragging');
      this.slider.setPointerCapture(this.pointerId);
    }

    event.preventDefault();

    const threshold = this.dragThreshold();
    if (delta <= -threshold) {
      this.finishSwipe(1);
      return;
    }
    if (delta >= threshold) {
      this.finishSwipe(-1);
      return;
    }

    this.slider.scrollLeft = this.dragScrollLeft - delta;
  }

  onPointerUp() {
    if (!this.tracking) return;
    const wasDragging = this.dragging;
    this.stopTracking();
    if (!wasDragging) return;
    this.goTo(this.activeIndex, true);
  }

  onWheel(event) {
    if (Math.abs(event.deltaX) < Math.abs(event.deltaY)) return;
    event.preventDefault();
    if (this.dragging) return;
    if (this.wheelLock) return;

    this.wheelLock = true;
    const next = event.deltaX > 0 ? this.activeIndex + this.swipeStep() : this.activeIndex - this.swipeStep();
    this.goTo(next, true);
    clearTimeout(this.wheelTimer);
    this.wheelTimer = setTimeout(() => {
      this.wheelLock = false;
    }, 180);
  }
}

customElements.define('homepage-ugc-reviews', HomepageUgcReviews);
